/**
 * Embedded (WebView) 認証ブリッジ
 *
 * モバイルアプリの WebView 内で本Webを表示する際、ネイティブ側から postMessage で
 * Cognito idToken を注入してもらい、API 認証に使う（Amplifyセッション非依存）。
 * - 初期表示: EMBED_READY をネイティブへ送出 → ネイティブが SET_TOKENS で idToken を返す。
 * - 期限切れ(60分): TOKEN_EXPIRED をネイティブへ送出 → ネイティブがリフレッシュして SET_TOKENS。
 * - 認証不能: UNAUTHORIZED をネイティブへ送出（ネイティブがQR画面へ誘導）。
 * ※ refreshToken はネイティブの secure-storage に留め、Webには渡さない（案①）。
 */

let injectedToken: string | null = null;
let waiters: Array<(t: string | null) => void> = [];
let listenerAttached = false;

/** ?embedded=1 で開かれているか */
export function isEmbedded(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('embedded') === '1';
  } catch {
    return false;
  }
}

export function getInjectedToken(): string | null {
  return injectedToken;
}

/** ネイティブから受け取った idToken を保存し、待機中の解決を発火 */
export function setInjectedToken(token: string | null): void {
  injectedToken = token;
  const current = waiters;
  waiters = [];
  current.forEach((w) => w(token));
}

function postToParent(msg: Record<string, unknown>): void {
  try {
    // 親はネイティブ WebView（オリジン非固定）のため '*'。受信は embedded 時のみ有効化。
    window.parent?.postMessage(msg, '*');
  } catch {
    /* ignore */
  }
}

/** 次に SET_TOKENS が来るまで待つ（TOKEN_EXPIRED は送らない・初期表示用）。timeout で null。 */
export function waitForToken(timeoutMs = 15000): Promise<string | null> {
  if (injectedToken) return Promise.resolve(injectedToken);
  return new Promise((resolve) => {
    let done = false;
    const finish = (t: string | null) => {
      if (done) return;
      done = true;
      resolve(t);
    };
    waiters.push(finish);
    setTimeout(() => finish(null), timeoutMs);
  });
}

/** ネイティブへリフレッシュ要求を送り、新トークン到着を待つ（401時用）。timeout で null。 */
export function requestTokenRefresh(timeoutMs = 10000): Promise<string | null> {
  postToParent({ type: 'TOKEN_EXPIRED' });
  return waitForToken(timeoutMs);
}

/** 認証不能をネイティブへ通知（QR画面へ誘導される） */
export function notifyUnauthorized(): void {
  postToParent({ type: 'UNAUTHORIZED' });
}

/** 回答完了をネイティブへ通知（ネイティブが WebView を閉じてタブへ戻す） */
export function notifyAnswerDone(): void {
  postToParent({ type: 'ANSWER_DONE' });
}

/** ?lang= で指定された表示言語（無ければ null）。embedded 時のみ意味を持つ。 */
export function getEmbeddedLang(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('lang');
  } catch {
    return null;
  }
}

/** ネイティブからの postMessage 受信を開始（EmbeddedShell マウント時）。戻り値で解除。 */
export function attachEmbeddedTokenListener(): () => void {
  if (listenerAttached) return () => undefined;
  listenerAttached = true;
  const handler = (event: MessageEvent) => {
    const data = event.data as { type?: string; idToken?: unknown } | null;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'SET_TOKENS' && typeof data.idToken === 'string') {
      setInjectedToken(data.idToken);
    }
  };
  window.addEventListener('message', handler);
  // 準備完了をネイティブへ通知（トークン送出のトリガ）
  postToParent({ type: 'EMBED_READY' });
  return () => {
    window.removeEventListener('message', handler);
    listenerAttached = false;
  };
}
